import * as React from 'react';
import { useSelector } from 'react-redux';

import type { AiCoach_ConfigurationInput as CoachConfigType } from '__generated__/graphql-types';

import { useLocation } from 'js/lib/useRouter';

import type { ClickAutocompleteSuggestions } from '@coursera/event-pulse-types';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import StoreProviderWrapper from 'bundles/ai-coach-platform/components/patterns/chat/store/StoreProviderWrapper';
import type { GlobalState } from 'bundles/ai-coach-platform/components/patterns/chat/store/types';
import {
  getChatSessionStorageKey,
  persistentChatSessionId,
} from 'bundles/ai-coach-platform/components/patterns/chat/utils/sessionUtils';
import { AiPromptPill } from 'bundles/search-common/components/AiPromptPill';
import {
  ASK_ANYTHING_EVENT_ID,
  ASK_ANYTHING_ROUTE_TYPE,
  DEFAULT_COACH_CONFIG,
  INTENT_TYPE_TO_COACH_CONFIG,
} from 'bundles/search-common/components/experiments/aiSearchOnLohp/constants';
import useAiPromptPillClick from 'bundles/search-common/hooks/useAiPromptPillClick';
import type { QueryPrompt } from 'bundles/search-common/providers/QueryPromptsProvider';
import { useSearchRefresh } from 'bundles/search-common/providers/SearchRefreshContext';
import { saveRecentlySearched } from 'bundles/search-common/utils/SearchUtils';

import _t from 'i18n!nls/front-page';

type Props = {
  prompt?: QueryPrompt;
  isAskAnything?: boolean;
};

const AiSearchCoachButtonContent = ({ prompt, isAskAnything }: Props) => {
  const location = useLocation();
  const { refreshRecentSearches } = useSearchRefresh() || {};

  const track = useTracker();
  const { isMessageLoading, isMessageStreaming } = useSelector((state: GlobalState) => {
    return {
      isMessageLoading: state.behavior.messageLoader,
      isMessageStreaming: state.behavior.isMessageStreaming,
    };
  });

  let label: string;
  let message: string;
  let coachConfig: CoachConfigType | undefined;

  if (isAskAnything) {
    label = _t('Ask anything');
    coachConfig = DEFAULT_COACH_CONFIG;
  } else {
    label = prompt?.promptTextShort ?? _t('Ask anything');
    coachConfig = INTENT_TYPE_TO_COACH_CONFIG[prompt?.intentType ?? ''] ?? DEFAULT_COACH_CONFIG;
  }

  const onCoachActionClick = useAiPromptPillClick({
    promptCoachConfig: { config: coachConfig },
    promptCoachRouterConfig: { config: DEFAULT_COACH_CONFIG },
  });

  const eventingData: ClickAutocompleteSuggestions = {
    page: {},
    queryPrompt: {
      id: isAskAnything ? ASK_ANYTHING_EVENT_ID : prompt?.id ?? '',
      location: 'lohp',
      promptText: label,
      intentType: prompt?.intentType,
      routeType: isAskAnything ? ASK_ANYTHING_ROUTE_TYPE : prompt?.routeType,
      routeParameter: prompt?.routeParameter,
    },
    suggestionType: 'ai_prompt',
    suggestionSection: 'ai_prompt',
  };

  const trackingRef: React.MutableRefObject<HTMLSpanElement | null> = useVisibilityTracker(
    'view_autocomplete_suggestions',
    eventingData,
    [],
    { fullyVisible: true, initialDelay: 1000 }
  );

  const handleClick = () => {
    track('click_autocomplete_suggestions', eventingData);

    const chatSessionId = persistentChatSessionId.get(getChatSessionStorageKey(location, ''));
    const askAnythingMessage = chatSessionId ? _t('I have another question.') : _t('I have a question.');

    if (isAskAnything) {
      message = askAnythingMessage;
    } else {
      message = prompt?.promptTextLong ?? askAnythingMessage;
      saveRecentlySearched(label, {
        isAiPrompt: true,
        routeType: prompt?.routeType,
        intentType: prompt?.intentType,
      });

      refreshRecentSearches?.();
    }

    onCoachActionClick({ message, label });
  };

  return (
    <span ref={trackingRef}>
      <AiPromptPill
        label={label}
        disabled={isMessageLoading || isMessageStreaming}
        onClick={handleClick}
        variant={isAskAnything ? 'alive' : 'white'}
      />
    </span>
  );
};

const AiSearchCoachButton = (props: Props) => {
  return (
    <StoreProviderWrapper>
      <AiSearchCoachButtonContent {...props} />
    </StoreProviderWrapper>
  );
};

export default AiSearchCoachButton;
